import { BehaviorSubject } from "rxjs";
import _ from "lodash/fp";

const subject = new BehaviorSubject(null);
let client;

const store = {
  client,
  /**
   * Fetch data from the graphQL API and write the response to the stream
   *
   * @param {string} id
   */
  fetch: (type) => (query) => async (params) => {
    // If this was called without a value for the type, send a null to the stream and exit
    if (!type || !query) {
      return subject.next({});
    }

    try {
      // Query the graphQL API
      const response = await client.query(query, params).toPromise();

      // Push the response onto the stream (even if nothing was returned)
      subject.next({
        response: {
          ...response,
          data: Object.keys(response.data || {})?.reduce((acc, queryName) => {
            if (response.data[queryName] === null)
              return { ...acc, [queryName]: null };
            return {
              ...acc,
              [queryName]: response.data[queryName].map((row) => {
                let name;
                let uri;
                switch (type) {
                  case "item":
                    name = row.passage?.id
                      ? `${row.passage.name}`
                      : `${row.name}`;
                    uri =
                      `/workspace?itemID=${row?.id}` +
                      (row.passage?.id ? `&passageID=${row.passage.id}` : ``);
                    break;
                  case "passage":
                    name = `${row.name}`;
                    uri = `/workspace?&passageID=${row.id}`;
                    break;
                  default:
                    console.warn(
                      `can't generate link uri/text - unknown type ${type}`,
                    );
                }
                const workspace = {
                  name,
                  uri,
                };
                return { ...row, workspace };
              }),
            };
          }, {}),
        },
        params,
        type,
      });

      // Throw an error if one is returned
      if (response.error) {
        throw new Error(response.error);
      }
    } catch (err) {
      console.error(err);
    }
  },
  /**
   * Gets the current value of the stream
   */
  get: () => {
    return subject.getValue();
  },
  init: (c) => {
    client = c;
  },
  /**
   * Write data to the stream
   */
  set: (value) => {
    subject.next(value);
  },

  delete: (type) => (query) => async (params) => {
    if (!type || !query) {
      return subject.next({});
    }
    try {
      // Query the graphQL API
      const response = await client.mutation(query, params).toPromise();
      const responseIds = response?.data?.[`delete${_.capitalize(type)}s`];
      const updatedResponse = subject
        .getValue()
        ?.response?.data?.[
          `${params.queryType}`
        ].filter((t) => !responseIds.map((ids) => ids?.id).includes(t?.id));

      // Push the response onto the stream (even if nothing was returned)
      subject.next({
        response: {
          data: {
            [`${params.queryType}`]: [...updatedResponse],
          },
        },
        params: subject.getValue()?.params, // preserve original search params
        type,
      });

      // Throw an error if one is returned
      if (response.error) {
        throw new Error(response.error);
      }
      return params.ids.filter(
        (id) => !responseIds.map((ids) => ids?.id).includes(id),
      );
    } catch (err) {
      console.error(err);
      return params.ids;
    }
  },
  /**
   * Subscribe to the stream
   *
   * @returns {Function} A function to unsubscribe from the stream
   */
  subscribe: (setState) => {
    return subject.subscribe(setState);
  },
};

export { store };
